import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { SettingsProvider } from './contexts/SettingsProvider';
import { TranslationProvider } from './translations';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import RefCaptureMount from './components/RefCaptureMount';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmergencyPasswordReset from './pages/EmergencyPasswordReset';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import PlanDebugger from './pages/PlanDebugger';
import PlanQAPlayground from './pages/PlanQAPlayground';
import AccountBilling from './pages/AccountBilling';
import ChoosePlan from './pages/ChoosePlan';
import TradingInterface from './pages/TradingInterface';
import Partnership from './pages/Partnership';
import FoundingMember from './pages/FoundingMember';
import FeedbackForm from './pages/FeedbackForm';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RiskDisclaimer from './pages/RiskDisclaimer';
import CookiesPolicy from './pages/CookiesPolicy';
import DefensivePublication from './pages/DefensivePublication';
import DefensivePublicationJournal from './pages/DefensivePublicationJournal';
import DefensivePublicationTDI from './pages/DefensivePublicationTDI';
import DefensivePublications from './pages/DefensivePublications';
import BugReportButton from './components/BugReportButton';

// Data Room pages
import DataRooms from './pages/DataRooms';
import DataRoomDetail from './pages/DataRoomDetail';
import CreateDataRoom from './pages/CreateDataRoom';
import DataRoomMembers from './pages/DataRoomMembers';
import DataRoom2FASetup from './pages/DataRoom2FASetup';

// Testimonials
import SubmitTestimonial from './pages/SubmitTestimonial';

// Public discipline test (viral hook — no signup)
import DisciplineTest from './pages/DisciplineTest';
import Leaderboard from './pages/Leaderboard';

// Affiliate program
import Affiliate from './pages/Affiliate';
import AffiliateTerms from './pages/AffiliateTerms';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';

// CTO Tasks
import CTOTasksDashboard from './pages/CTOTasksDashboard';

import Footer from './components/Footer';
import './App.css';
// Hide the global Navbar on routes that ship their own (landing,
// login, register, /test all use a slim custom header). /admin also
// ships its own header (AdminHeader on desktop + a mobile sticky top
// bar with a hamburger), so the global Navbar would duplicate that
// role and leave a stray pink/red hairline across the top of the
// admin shell.
function ConditionalNavbar() {
  const location = useLocation();
  const hideOnRoutes = ['/', '/test', '/login', '/register'];
  if (hideOnRoutes.includes(location.pathname)) return null;
  if (location.pathname.startsWith('/admin')) return null;
  return <Navbar />;
}

// Hide the marketing Footer inside the admin shell. The admin has its
// own compact "System Online" status strip inside AdminSidebar and a
// full-width SnapChart marketing footer under the panels looks broken
// and off-brand there. Everywhere else the Footer stays.
function ConditionalFooter() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <Footer />;
}

function App() {
  return (
    <TranslationProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <SettingsProvider>
            <Router>
              <ScrollToTop />
              <RefCaptureMount />
              <div className="flex flex-col min-h-screen bg-gray-900">
                <ConditionalNavbar />
                <div className="flex-grow">
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/emergency-reset" element={<EmergencyPasswordReset />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/risk-disclaimer" element={<RiskDisclaimer />} />
                    <Route path="/cookies" element={<CookiesPolicy />} />
                    <Route path="/legal/defensive-publications" element={<DefensivePublications />} />
                    <Route path="/legal/defensive-publication-plutus" element={<DefensivePublication />} />
                    <Route path="/legal/defensive-publication-journal" element={<DefensivePublicationJournal />} />
                    <Route path="/legal/defensive-publication-tdi" element={<DefensivePublicationTDI />} />
                    <Route path="/partnership" element={<Partnership />} />
                    {/* Founding Member scarcity offer — Growth Plan §2.2. Public. */}
                    <Route path="/founding" element={<FoundingMember />} />
                    {/* Public feedback form — receives replies from broadcast
                        emails that embed {{feedback_url}}. Anon-safe: RPC
                        verifies HMAC token so random visitors can't spam. */}
                    <Route path="/feedback" element={<FeedbackForm />} />
                    <Route path="/submit-testimonial" element={<SubmitTestimonial />} />
                    <Route path="/test" element={<DisciplineTest />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
                    <Route path="/affiliate/terms" element={<AffiliateTerms />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                    
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requireAdmin={true}>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/debugger"
                      element={
                        <ProtectedRoute requireAdmin={true}>
                          <PlanDebugger />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/plan-qa"
                      element={
                        <ProtectedRoute requireAdmin={true}>
                          <PlanQAPlayground />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/cto-tasks"
                      element={
                        <ProtectedRoute>
                          <CTOTasksDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/account/billing"
                      element={
                        <ProtectedRoute>
                          <AccountBilling />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/choose-plan"
                      element={
                        <ProtectedRoute>
                          <ChoosePlan />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/trading"
                      element={
                        <ProtectedRoute requireSubscription={true}>
                          <TradingInterface />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Data Room routes */}
                    <Route
                      path="/data-rooms"
                      element={
                        <ProtectedRoute>
                          <DataRooms />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/data-rooms/create"
                      element={
                        <ProtectedRoute>
                          <CreateDataRoom />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/data-rooms/setup-2fa"
                      element={
                        <ProtectedRoute>
                          <DataRoom2FASetup />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/data-rooms/:id"
                      element={
                        <ProtectedRoute>
                          <DataRoomDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/data-rooms/:id/members"
                      element={
                        <ProtectedRoute>
                          <DataRoomMembers />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </div>
                <ConditionalFooter />
                {/* BugReportButton removed from public surface — was visible
                    to every user as a bright orange chip on every page which
                    broke the $1B premium feel. Now reachable only via the
                    admin /admin -> Bug Reports panel. */}
              </div>
            </Router>
          </SettingsProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </TranslationProvider>
  );
}

export default App;